import type * as Types from '@aeppic/types'
import type { AeppicInterface } from '../aeppic.js'
import { navigationQuery } from './navigation-query.js'

export const navigationForms = {
  instanceMenu: '803d49db-a422-43a4-8ad7-a23df5830de6',
  applicationMenu: '37f45ee6-a81c-495a-80da-9f77c2d1dac8',
  applicationMenuEntry: '3f31c496-231f-42dc-8f25-989d44087b01',
}

export interface NavigationLookupEntry {
  id: string
  label: string
  icon: string | null
  entry: Types.Reference
  target: Types.Reference | null
  params: any
  children: NavigationLookupEntry[]
  /** Independent of the item's target mode; false even when a dynamic source is currently empty. */
  childrenComplete: boolean
}

export interface NavigationLookupChildren {
  children: NavigationLookupEntry[]
  childrenComplete: boolean
}

export interface NavigationLookupApplication {
  reference: Types.Reference
  rootAncestorId: string
  name: string
  icon: string
  navigation: NavigationLookupEntry[]
  navigationComplete: boolean
}

export interface NavigationLookupResult {
  rootId: string
  workspace: Types.Reference
  instanceMenu: Types.Reference | null
  applications: NavigationLookupApplication[]
}

const text = (value: unknown): string => typeof value === 'string' ? value.trim() : ''
const reference = (document: Types.Document): Types.Reference => ({
  id: document.id, v: document.v, text: text((document as Types.Document & { text?: string }).text) || text(document.data?.name),
})
const label = (document: Types.Document): string => text(document?.data?.label)
  || text(document?.data?.name) || text(document?.f?.text) || document?.id || ''

/** Reads only through the supplied API: local and authenticated server resolution share this code. */
export class NavigationLookup {
  private documents = new Map<string, Promise<Types.Document>>()
  private searches = new Map<string, Promise<Types.Document[]>>()

  constructor(
    private api: Pick<AeppicInterface, 'get' | 'find' | 'findOne' | 'Query'>,
    private concurrent: () => boolean = () => true,
  ) {}

  private async collect<T, R>(values: T[], read: (value: T) => Promise<R> | R): Promise<R[]> {
    if (this.concurrent()) return Promise.all(values.map(read))
    const results: R[] = []
    for (const value of values) results.push(await read(value))
    return results
  }

  private get(identifier: Types.IdOrReference): Promise<Types.Document> {
    const id = typeof identifier === 'string' ? identifier : identifier?.id
    if (!id) return Promise.resolve(null)
    // Menu references are identity-first; current documents determine current navigation.
    if (!this.documents.has(id)) this.documents.set(id, this.api.get(id))
    return this.documents.get(id)
  }

  private async getAll(references: Types.IdOrReference[] = []): Promise<Types.Document[]> {
    const documents = await this.collect(references, ref => this.get(ref))
    return documents.filter(Boolean)
  }

  async resolve(rootId: string): Promise<NavigationLookupResult> {
    const workspace = await this.get(rootId)
    if (!workspace) throw Object.assign(new Error('Navigation root not found'), { status: 404 })

    const instanceMenu = await this.api.findOne(
      this.api.Query.children(rootId).form(navigationForms.instanceMenu), { includeHidden: true },
    )
    const result: NavigationLookupResult = {
      rootId, workspace: reference(workspace), instanceMenu: null, applications: [],
    }
    if (!instanceMenu) return result
    result.instanceMenu = reference(instanceMenu)

    const applications = await this.getAll(instanceMenu.data.applications as Types.Reference[] || [])
    result.applications = await this.collect(applications, async application => {
      const entries = await this.getAll(application.data.menuEntries as Types.Reference[] || [])
      const navigation = await this.buildEntries(workspace, application, entries, 0, new Set())
      return {
        reference: reference(application),
        rootAncestorId: application.p,
        name: text(application.data.label) || text(application.data.name),
        icon: text(application.data.cssIcon),
        navigation: navigation.children,
        navigationComplete: navigation.childrenComplete,
      }
    })
    return result
  }

  /** A fresh resolver checks this branch through the normal authenticated/local query path. */
  async resolveChildren(rootId: string, applicationId: string, entryId: string): Promise<NavigationLookupChildren> {
    const [workspace, application, entry] = await this.collect([rootId, applicationId, entryId], id => this.get(id))
    if (!workspace || !application || !entry) throw new Error('Navigation child source not found')
    return this.entryChildren(workspace, application, entry, 0, new Set([entry.id]))
  }

  async resolveApplication(rootId: string, applicationId: string): Promise<NavigationLookupChildren> {
    const [workspace, application] = await this.collect([rootId, applicationId], id => this.get(id))
    if (!workspace || !application) throw new Error('Navigation application not found')
    const entries = await this.getAll(application.data.menuEntries as Types.Reference[] || [])
    return this.buildEntries(workspace, application, entries, 0, new Set())
  }

  private async entryChildren(workspace: Types.Document, app: Types.Document, entry: Types.Document, depth: number, path: Set<string>): Promise<NavigationLookupChildren> {
    const child = await this.get(entry.data.children as Types.Reference)
    if (!child) return { children: [], childrenComplete: true }
    return this.buildEntries(workspace, app, [child], depth + 1, path)
  }

  private async dynamicTargets(workspace: Types.Document, app: Types.Document, entry: Types.Document) {
    const root = (entry.data.target as Types.Reference)?.id || app.p || workspace.id
    let base = this.api.Query.children(root)
    const scope = text(entry.data.dynamicTargetScope)
    if (scope === 'GLOBAL') base = this.api.Query.global()
    if (scope === 'DESCENDANTS') base = this.api.Query.descendants(root)
    if (scope === 'HIGHEST_ANCESTOR') base = this.api.Query.descendants(workspace.id)

    const query = navigationQuery(base, text(entry.data.dynamicTargetQuery)).toQuery()
    const options = { size: entry.data.singleDynamicTarget ? 1 : 1000 }
    const key = JSON.stringify([query, options])
    if (!this.searches.has(key)) this.searches.set(key, this.api.find(query, options))
    const targets = await this.searches.get(key)
    return [...targets].sort((a, b) => label(a).localeCompare(label(b), 'de'))
  }

  private async buildEntries(workspace: Types.Document, app: Types.Document, entries: Types.Document[], depth: number, ancestors: Set<string>): Promise<NavigationLookupChildren> {
    const groups = await this.collect(entries, async entry => {
      if (ancestors.has(entry.id) || ancestors.size >= 64) {
        throw new Error(`Cyclic or excessively deep navigation at ${entry.id}`)
      }
      const path = new Set(ancestors).add(entry.id)
      const children = () => this.entryChildren(workspace, app, entry, depth, path)
      const makeEntry = (target: Types.Document, nested: NavigationLookupChildren, dynamic: boolean): NavigationLookupEntry => ({
        id: dynamic ? `${entry.id}:${target.id}` : entry.id,
        label: dynamic ? label(target) : text(entry.data.label) || text(entry.data.name) || entry.id,
        icon: text(entry.data.cssIcon) || text(dynamic && target?.data?.cssIcon) || 'fa-solid fa-folder',
        entry: reference(entry),
        target: target ? reference(target) : null,
        params: entry.data.params ?? null,
        children: nested.children,
        childrenComplete: nested.childrenComplete,
      })
      if (entry.data.targetMode === 'QUERY') {
        const [targets, nested] = await this.collect<() => Promise<any>, any>([
          () => this.dynamicTargets(workspace, app, entry), children,
        ], read => read())
        const resolved = await this.collect<Types.Document, NavigationLookupEntry[]>(targets, async target => {
          if (target.f.id === navigationForms.applicationMenuEntry) {
            const collection = await this.buildEntries(workspace, app, [target], depth, path)
            return collection.children
          }
          return [makeEntry(target, nested, true)]
        })
        return { children: resolved.flat(), childrenComplete: false }
      }
      const [target, nested] = await this.collect<() => Promise<any>, any>([
        () => this.get(entry.data.target as Types.Reference), children,
      ], read => read())
      // Do not expose inaccessible targets through cached references.
      if ((entry.data.target as Types.Reference)?.id && !target && !nested.children.length && nested.childrenComplete) {
        return { children: [], childrenComplete: true }
      }
      return { children: [makeEntry(target, nested, false)], childrenComplete: true }
    })
    let navigation = groups.flatMap(group => group.children)
    if (depth > 0 && navigation.length > 1 && navigation.every(item => item.icon && item.icon === navigation[0].icon)) {
      navigation = navigation.map(item => ({ ...item, icon: null }))
    }
    return { children: navigation, childrenComplete: groups.every(group => group.childrenComplete) }
  }
}
